import { throttle } from "lodash";
import { useEffect, useLayoutEffect, useMemo } from "react";

interface UseChatMessageListScrollHandlerOptions {
  /** Whether the first batch of messages has finished loading. */
  isInitialized: boolean;
  /**
   * Whether the message list is currently mounted/shown.
   * When it becomes true again (e.g. leaving voice mode), scroll is restored to the bottom.
   */
  isVisible: boolean;
  /** Scrollable message list container. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Called when the user scrolls near the top of the list (e.g. to load older messages). */
  onReachEnd?: () => void;
}

/**
 * Handles scrolling behavior for the chat message list:
 * - Instant scroll to the bottom once the chat is initialized
 * - Restore scroll to the bottom when the list becomes visible again
 * - Call `onReachEnd` whenever user reaches the end of the list (useful for infinite scroll mechanisms)
 */
export const useChatMessageListScrollHandler = ({
  isInitialized,
  isVisible,
  containerRef,
  onReachEnd,
}: UseChatMessageListScrollHandlerOptions) => {
  const handleScrollThrottled = useMemo(
    () =>
      throttle((event: Event) => {
        const container = event.target as HTMLElement | null;

        if (container && container.scrollTop < 200) {
          onReachEnd?.();
        }
      }, 500),
    [onReachEnd]
  );

  // Instant scroll to the bottom on first load, and again when the list remounts/becomes visible
  useLayoutEffect(() => {
    if (!isInitialized || !isVisible) {
      return;
    }

    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: "instant",
    });
  }, [containerRef, isInitialized, isVisible]);

  // Call `onReachEnd` whenever the user scrolls to within 200px of the top of the list
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    container.addEventListener("scroll", handleScrollThrottled);

    return () => {
      container.removeEventListener("scroll", handleScrollThrottled);
      handleScrollThrottled.flush();
    };
  }, [onReachEnd, handleScrollThrottled, containerRef]);
};
